import { useCallback, useEffect, useRef, useState } from 'react';
import {
  VOICE_MAX_DURATION_SEC,
  VOICE_MIN_DURATION_SEC,
  createVoiceMessageFile,
  mapAnalyserToBars,
  pickVoiceRecorderMime,
} from '../utils/supportVoice';

export type VoiceRecorderPhase = 'idle' | 'requesting' | 'recording' | 'preview';

export interface VoiceRecorderResult {
  file: File;
  previewUrl: string;
  durationSec: number;
}

interface Options {
  maxDurationSec?: number;
  minDurationSec?: number;
  onError?: (message: string) => void;
}

const BAR_COUNT = 28;

export function useVoiceRecorder(options: Options = {}) {
  const maxDurationSec = options.maxDurationSec ?? VOICE_MAX_DURATION_SEC;
  const minDurationSec = options.minDurationSec ?? VOICE_MIN_DURATION_SEC;
  const onError = options.onError;

  const [phase, setPhase] = useState<VoiceRecorderPhase>('idle');
  const [durationSec, setDurationSec] = useState(0);
  const [liveBars, setLiveBars] = useState<number[]>(() => Array(BAR_COUNT).fill(0.2));
  const [preview, setPreview] = useState<VoiceRecorderResult | null>(null);

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const mimeRef = useRef<string>('audio/webm');
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const timerRef = useRef<number | null>(null);
  const startedAtRef = useRef<number>(0);
  const previewUrlRef = useRef<string | null>(null);

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const stopAnalyserLoop = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (audioCtxRef.current) {
      try {
        void audioCtxRef.current.close();
      } catch {}
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
  }, []);

  const stopTimer = useCallback(() => {
    if (timerRef.current != null) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const revokePreview = useCallback(() => {
    if (previewUrlRef.current) {
      try {
        URL.revokeObjectURL(previewUrlRef.current);
      } catch {}
      previewUrlRef.current = null;
    }
  }, []);

  const reset = useCallback(() => {
    stopTimer();
    stopAnalyserLoop();
    stopTracks();
    recorderRef.current = null;
    chunksRef.current = [];
    revokePreview();
    setPreview(null);
    setDurationSec(0);
    setLiveBars(Array(BAR_COUNT).fill(0.2));
    setPhase('idle');
  }, [revokePreview, stopAnalyserLoop, stopTimer, stopTracks]);

  const startAnalyserLoop = useCallback((stream: MediaStream) => {
    const Ctx = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const source = ctx.createMediaStreamSource(stream);
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.65;
    source.connect(analyser);
    audioCtxRef.current = ctx;
    analyserRef.current = analyser;

    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      analyser.getByteFrequencyData(data);
      setLiveBars(mapAnalyserToBars(data, BAR_COUNT));
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const finishRecording = useCallback(() => {
    const elapsed = (Date.now() - startedAtRef.current) / 1000;
    stopTimer();
    stopAnalyserLoop();
    stopTracks();

    const blob = new Blob(chunksRef.current, { type: mimeRef.current });
    chunksRef.current = [];

    if (elapsed < minDurationSec) {
      onError?.(`Голосовое слишком короткое (минимум ${minDurationSec} с)`);
      reset();
      return;
    }

    if (blob.size === 0) {
      onError?.('Не удалось записать голосовое сообщение');
      reset();
      return;
    }

    revokePreview();
    const previewUrl = URL.createObjectURL(blob);
    previewUrlRef.current = previewUrl;
    const file = createVoiceMessageFile(blob, mimeRef.current);
    const result: VoiceRecorderResult = {
      file,
      previewUrl,
      durationSec: Math.round(elapsed),
    };
    setPreview(result);
    setDurationSec(Math.round(elapsed));
    setPhase('preview');
  }, [minDurationSec, onError, reset, revokePreview, stopAnalyserLoop, stopTimer, stopTracks]);

  const startRecording = useCallback(async () => {
    if (phase !== 'idle') return;

    if (!navigator.mediaDevices?.getUserMedia) {
      onError?.('Запись голоса не поддерживается в этом браузере');
      return;
    }

    const mime = pickVoiceRecorderMime();
    if (!mime) {
      onError?.('Запись голоса не поддерживается в этом браузере');
      return;
    }

    setPhase('requesting');
    mimeRef.current = mime;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      streamRef.current = stream;
      chunksRef.current = [];

      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        finishRecording();
      };
      recorder.onerror = () => {
        onError?.('Ошибка записи');
        reset();
      };

      recorder.start(200);
      startedAtRef.current = Date.now();
      setDurationSec(0);
      setPhase('recording');
      startAnalyserLoop(stream);

      timerRef.current = window.setInterval(() => {
        const elapsed = (Date.now() - startedAtRef.current) / 1000;
        setDurationSec(Math.floor(elapsed));
        if (elapsed >= maxDurationSec) {
          if (recorderRef.current?.state === 'recording') {
            recorderRef.current.stop();
          }
        }
      }, 200);
    } catch (err: unknown) {
      const name = err instanceof DOMException ? err.name : '';
      if (name === 'NotAllowedError' || name === 'PermissionDeniedError') {
        onError?.('Нет доступа к микрофону. Разрешите использование микрофона в настройках браузера.');
      } else if (name === 'NotFoundError') {
        onError?.('Микрофон не найден. Подключите микрофон и попробуйте снова.');
      } else {
        onError?.('Не удалось начать запись');
      }
      reset();
    }
  }, [finishRecording, maxDurationSec, onError, phase, reset, startAnalyserLoop]);

  const stopRecording = useCallback(() => {
    if (phase !== 'recording' || !recorderRef.current) return;
    if (recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  }, [phase]);

  const cancelRecording = useCallback(() => {
    if (phase === 'recording' && recorderRef.current?.state === 'recording') {
      recorderRef.current.onstop = null;
      recorderRef.current.stop();
    }
    reset();
  }, [phase, reset]);

  const discardPreview = useCallback(() => {
    revokePreview();
    setPreview(null);
    setDurationSec(0);
    setPhase('idle');
  }, [revokePreview]);

  const takePreview = useCallback((): VoiceRecorderResult | null => {
    if (!preview) return null;
    const taken = preview;
    previewUrlRef.current = null;
    setPreview(null);
    setPhase('idle');
    return taken;
  }, [preview]);

  useEffect(() => () => {
    reset();
  }, [reset]);

  return {
    phase,
    durationSec,
    liveBars,
    preview,
    startRecording,
    stopRecording,
    cancelRecording,
    discardPreview,
    takePreview,
    reset,
    isActive: phase === 'requesting' || phase === 'recording' || phase === 'preview',
  };
}
